import {
  ChangeEvent,
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  forwardRef,
} from 'react';

type ReusableInputProps = (
  | (Omit<InputHTMLAttributes<HTMLInputElement>, 'onChange'> & {
      type?:
        | 'text'
        | 'email'
        | 'password'
        | 'number'
        | 'tel'
        | 'url'
        | 'search';
      rows?: never;
    })
  | (Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'onChange'> & {
      type: 'textarea';
      rows?: number;
    })
) & {
  value?: string;
  onChange?: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
};

const InputField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  ReusableInputProps
>(
  (
    {
      value,
      onChange,
      placeholder = '',
      error = '',
      disabled = false,
      type = 'text',
      className = '',
      rows = 4,
      ...props
    },
    ref
  ) => {
    const sharedClasses = `
  w-full
  text-[0.9rem] font-sans font-extralight tracking-wider
  transition-colors duration-150
  ${disabled ? 'text-[#7a7268] cursor-not-allowed' : 'text-[#1c1a17]'}
  placeholder:text-[#7a7268]
  outline-none
  focus:ring-0
  ${className}
`;

    const inputClasses = `
  ${sharedClasses}
  px-0 py-2
  border-0 border-b bg-transparent
  ${
    error
      ? 'border-red-400/60 focus:border-red-400'
      : 'border-[rgba(184,154,94,0.3)] hover:border-[rgba(184,154,94,0.5)] focus:border-[#b89a5e]'
  }
`;

    const textareaClasses = `
  ${sharedClasses}
  px-3.5 py-2.5
  border
  ${disabled ? 'bg-[#e8e2d9]/50' : 'bg-[#e8e2d9]'}
  ${
    error
      ? 'border-red-400/60 focus:border-red-400 focus:ring-4 focus:ring-red-400/35'
      : 'border-[rgba(184,154,94,0.3)] hover:border-[rgba(184,154,94,0.5)] focus:border-[#b89a5e] focus:ring-4 focus:ring-[rgba(184,154,94,0.35)] focus:bg-[#f0ebe0]'
  }
`;

    return (
      <div className="w-full">
        {type === 'textarea' ? (
          <textarea
            ref={ref as React.Ref<HTMLTextAreaElement>}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            rows={rows}
            className={`${textareaClasses} resize-y min-h-[80px]`}
            {...(props as Omit<
              TextareaHTMLAttributes<HTMLTextAreaElement>,
              | 'onChange'
              | 'value'
              | 'placeholder'
              | 'disabled'
              | 'rows'
              | 'className'
            >)}
          />
        ) : (
          <input
            ref={ref as React.Ref<HTMLInputElement>}
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            disabled={disabled}
            className={inputClasses}
            {...(props as Omit<
              InputHTMLAttributes<HTMLInputElement>,
              | 'onChange'
              | 'value'
              | 'placeholder'
              | 'disabled'
              | 'type'
              | 'className'
            >)}
          />
        )}
        {error && (
          <p className="mt-1.5 text-[0.72rem] tracking-[0.03em] text-red-400 flex items-center gap-1">
            <span>⚠</span>
            {error}
          </p>
        )}
      </div>
    );
  }
);

InputField.displayName = 'InputField';

export default InputField;
